// Project utility functions

/**
 * Formats a project name with its ID for display
 * @param {Object} project - The project object
 * @param {string} project.id - The project ID
 * @param {string} project.name - The project name
 * @returns {string} Formatted project name with ID
 */
export const formatProjectName = (project) => {
  if (!project) return 'Unknown Project';
  
  // Create a nice 2-letter + 2-number format from the ID
  const shortId = project.id ? generateProjectCode(project.id) : 'NEW';
  return `#${shortId} - ${project.name || 'Unnamed Project'}`;
};

/**
 * Generates a nice project code in format AB12 from a document ID
 * @param {string} documentId - The Firestore document ID
 * @returns {string} Project code like AB12
 */
const generateProjectCode = (documentId) => {
  if (!documentId) return 'NEW';
  
  // Create multiple hash values from different parts of the ID to ensure uniqueness
  let hash1 = 0;
  let hash2 = 0;
  let hash3 = 0;
  
  for (let i = 0; i < documentId.length; i++) {
    const char = documentId.charCodeAt(i);
    hash1 = ((hash1 << 5) - hash1 + char) & 0x7FFFFFFF;
    hash2 = ((hash2 << 3) - hash2 + char * (i + 1)) & 0x7FFFFFFF;
    hash3 = ((hash3 << 7) - hash3 + char * (i + 2)) & 0x7FFFFFFF;
  }
  
  // Generate 2 letters (A-Z) using first hash
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const letter1 = letters[hash1 % 26];
  const letter2 = letters[hash2 % 26];
  
  // Generate 2 numbers (10-99) using third hash with better distribution
  const numbers = (hash3 % 90) + 10;
  
  return `${letter1}${letter2}${numbers}`;
};

/**
 * Formats just the project number for display
 * @param {string} projectId - The project ID
 * @returns {string} Formatted project number
 */
export const formatProjectNumber = (projectId) => {
  if (!projectId) return '#NEW';
  return `#${generateProjectCode(projectId)}`;
};

/**
 * Gets a short project reference for compact displays
 * @param {Object} project - The project object
 * @returns {string} Short project reference
 */
export const getProjectReference = (project) => {
  if (!project) return '#NEW';
  const shortId = project.id ? generateProjectCode(project.id) : 'NEW';
  return `#${shortId}`;
};